import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import _ from "lodash";
import Schemas from "schemas";
import styled, { css } from "styled-components";

import MainInfoPage from "./edit-1-main-info-page";
import RoomsPage from "./edit-2-rooms-page";
import EventsPage from "./edit-3-events-page";
import RulesPage from "./edit-4-rules-page";

import EditRoomModal from "components/modals/edit-room-modal";
import UpdateProgramModal from "components/modals/update-program-modal";
import { DormitoryTab } from "components/pages/dormitories-page";
import { Control } from "components/ui-kit/control";
import { AuthStyles, Button, Container, Form, Frame, Row, RowWrapper } from "components/ui-kit/styled-templates";

import { dormitoryQuestionnaireSteps as steps } from "constants/config";

import DormitoriesApi from "api/dormitories-api";
import RoomsApi from "api/rooms-api";

import useFormControl from "hooks/useFormControl";
import useStepsOptions from "hooks/useStepsOptions";
import { useStorageListener } from "hooks/useStorage";

import { EForms, ESteps } from "types/enums";
import { data } from "autoprefixer";

const stepStates = ["prev", "current", "next"] as const;
type TStepStates = typeof stepStates[number];

const Step = (props: { title: string; name: ESteps; state: TStepStates }) => {
    const { t } = useTranslation();

    const { title = ``, state = `prev`, name } = props;
    const { data: dormitoryData } = useFormControl({ name: EForms.dormitoryInfo });
    const { setCurrentStep, progress } = useStepsOptions(dormitoryData);
    const hasError = state === `prev` && progress[name] === false;
    const nameOfFirstUndone = steps[steps.map((i) => progress[i.name]).indexOf(false)]?.name;
    return (
        <StepStyles.Wrapper
            extra={state === `prev` || nameOfFirstUndone === name ? `cursor: pointer;` : `cursor: not-allowed;`}
            onClick={() => (state === `prev` || nameOfFirstUndone === name) && setCurrentStep(name)}
        >
            <StepStyles.StatusLine state={state} error={hasError} />
            <Row extra={`margin-top: 5px; color: ${hasError ? `red` : `#000`};`}>
                <StepStyles.Radio state={state} error={hasError} />
                {title}
            </Row>
        </StepStyles.Wrapper>
    );
};

const forms = (t: any) => ({
    [ESteps.MainInfo]: MainInfoPage,
    [ESteps.Rooms]: RoomsPage,
    [ESteps.Services]: EventsPage,
    [ESteps.Rules]: RulesPage,
    [ESteps.Documents]: () => (
        <>
            <Control.File
                multiple
                name={`${ESteps.Documents}`}
                accept="application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                label={t("ds43d6ma6ie")}
                dropzoneProps={{ label: 'Загрузить документы (размер не более 5Мб, формат PDF, DOC, TXT)', icon: "dropzone-file" }}
            />
        </>
    ),
});

const EditDormitoryPage = () => {
    const { t } = useTranslation();

    const { dormitoryId } = useParams();
    const dormitoryData = useStorageListener((state) => _.omit(_.get(state, `dormitories.${dormitoryId}`), `updatedTimestamp`));
    const { currentStep, goToNextStep, goToPrevStep, progress } = useStepsOptions(dormitoryData);
    const { data: formData, onSubmit, setValues, clearForm } = useFormControl({ name: EForms.dormitoryInfo, schema: Schemas.dormitory[currentStep] });
    const navigate = useNavigate();

    const currentStepNumber = steps.findIndex((step) => step.name === currentStep);

    const handlers = {
        submit: async (data: Object) => {
            await DormitoriesApi.updateDormitory({
                ...(dormitoryData ?? {}),
                details: { ...(dormitoryData.details ?? {}), ...data },
            });
            if (currentStepNumber === steps.length - 1) {
                navigate(`/dormitories`);
            } else {
                goToNextStep();
            }
        },
    };

    // @ts-ignore
    useEffect(async () => {
        if (_.isEmpty(dormitoryData)) {
            DormitoriesApi.getMyDormitories();
        } else {
            RoomsApi.getDormitoryRooms(dormitoryData.id);
            setValues(dormitoryData?.details ?? {});
        }
        return clearForm;
    }, [dormitoryData]);

    const SelectedForm = forms(t)[currentStep];

    const anyChanges = !_.isEqual(formData, dormitoryData.details);

    if (currentStepNumber === -1) return null;

    console.log('render: dormitoryData = ', dormitoryData);

    return (
        <>
            <UpdateProgramModal />
            <EditRoomModal />

            <StepsWrapper>
                <Container>
                    <Row extra={`width: inherit; gap: 4px;`}>
                        {steps.map((step, index) => (
                            <Step
                                {...step}
                                state={index === currentStepNumber ? `current` : index < currentStepNumber || progress[step.name] ? `prev` : `next`}
                                key={index}
                            />
                        ))}
                    </Row>
                </Container>
            </StepsWrapper>
            <Container extra={`max-width: 991px;`}>
                <AuthStyles.Container extra={`margin-top: 40px; width: inherit;`}>
                    <Form name={EForms.dormitoryInfo} onSubmit={onSubmit(handlers.submit)}>
                        <Control.Row extra={`h2 { margin: 0; fonr-size: 40px; }; margin-bottom: 30px;`}>
                            <h2>{_.get(steps, `[${currentStepNumber}].title`)}</h2>
                        </Control.Row>
                        <SelectedForm {...dormitoryData} accessibilityEnabled={formData?.accessibilityEnabled}
                            isRussia={formData?.country == 'РОССИЯ'}
                        />
                        {anyChanges && <Control.Error extra={`margin-bottom: 15px;`}>{t("wedqo6q6bre")}</Control.Error>}
                        <RowWrapper extra={`${currentStepNumber < 1 && `justify-content: flex-end;`}`}>
                            {currentStepNumber > 0 && <ArrowLeft onClick={goToPrevStep}>{t("99tfgpg47u")}</ArrowLeft>}
                            <Button type="submit">{t("ips3plsfvpf")}</Button>
                        </RowWrapper>
                    </Form>
                </AuthStyles.Container>
            </Container>
        </>
    );
};

const ArrowLeft = styled(DormitoryTab.Link).attrs((props: any) => ({ ...props, as: `div`, color: `blue`, direction: `left` } as any))``;

const StepStyles = {
    Wrapper: styled(Frame)`
        width: 100%;
        align-items: flex-start;
    `,
    StatusLine: styled(Frame)<{ state: TStepStates; error: boolean }>`
        width: 100%;
        border-radius: 4px;
        height: 4px;
        background-color: ${({ theme, state, error }) =>
            ({
                prev: error ? theme.red : `#697386`,
                current: theme.blue,
                next: `#C1C9D2`,
            }[state])};
    `,
    Radio: styled(Frame)<{ state: TStepStates; error: boolean }>`
        width: 16px;
        height: 16px;
        border-radius: 9999px;
        background: ${({ theme, state, error }) =>
            ({
                prev: error ? theme.red : `#697386`,
                current: theme.blue,
                next: `#8792A2`,
            }[state])};
        position: relative;
        margin-right: 5px;

        &:after {
            content: "";
            display: flex;
            position: absolute;
            top: 50%;
            left: 50%;
            width: 12px;
            height: 12px;
            border-radius: 9999px;
            background: #fff;
            transition: 0.2s;
            transform: translate(-50%, -50%)
                scale(
                    ${({ state, error }) =>
                        ({
                            prev: error ? 1 : 0.8,
                            current: 0.5,
                            next: 1,
                        }[state])}
                );

            ${({ state, error }) =>
                state === `prev` &&
                css`
                    background: url("${require(`assets/icons/${error ? `cross-white` : `check-mark-white`}.svg`)}") no-repeat center center / contain;
                `}
        }
    `,
};

const StepsWrapper = styled(Frame)`
    width: 100%;
    padding: 30px 0;
    box-sizing: border-box;
    background: #fff;
`;

export default EditDormitoryPage;
